import { z } from "zod";
import { FileTag, FileTagSchema } from "@remi/enums/file-tag";

const FileTypeValues = ["DOCUMENT", "PHOTO", "AUDIO", "VIDEO"] as const;
export const FileTypeSchema = z.enum(FileTypeValues);
export type FileType = (typeof FileTypeValues)[number];
export const FileType = FileTypeSchema.enum;

const FileRoleValues = [
  "QUOTE",
  "INVOICE",
  "QUOTE_IMAGE",
  "SKETCH",
  "EAGLEVIEW",
  "CORRECTIONS_PHOTO",
  "PRICING_SHEET",
  "MARKET_SHEET",
  "CONTRACTOR_MARKET_SHEET",
  "PROFILE_PHOTO",
  "W_9",
  "CONTRACTOR_LICENSE",
  "SAFETY_HEALTH_POLICY",
  "FULL_POLICY",
  "MASTER_ROOFING_SERVICES_AGREEMENT",
  "INSURANCE_CERTIFICATE",
  "WORK_ORDER",
  "ACH",
  "DRIVERS_LICENSE",
  "GENERAL_LIABILITY_INSURANCE",
  "GENERAL_LIABILITY_FULL_POLICY",
  "GENERAL_LIABILITY_ADDITIONAL_INSURED",
  "GENERAL_LIABILITY_WAIVER_OF_SUBROGATION",
  "GENERAL_LIABILITY_PRIMARY_NON_CONTRIBUTORY",
  "GENERAL_LIABILITY_NOTICE_OF_CANCELLATION",
  "AUTO_INSURANCE",
  "AUTO_LIABILITY_FULL_POLICY",
  "AUTO_ADDITIONAL_INSURED",
  "AUTO_WAIVER_OF_SUBROGATION",
  "AUTO_PRIMARY_NON_CONTRIBUTORY",
  "AUTO_NOTICE_OF_CANCELLATION",
  "UMBRELLA_LIABILITY_INSURANCE",
  "UMBRELLA_LIABILITY_FULL_POLICY",
  "UMBRELLA_ADDITIONAL_INSURED",
  "UMBRELLA_WAIVER_OF_SUBROGATION",
  "UMBRELLA_PRIMARY_NON_CONTRIBUTORY",
  "UMBRELLA_NOTICE_OF_CANCELLATION",
  "UMBRELLA_FOLLOW_FORM_DOCUMENT",
  "WORKERS_COMPENSATION_INSURANCE",
  "WORKERS_COMPENSATION_FULL_POLICY",
  "WORKERS_COMPENSATION_ADDITIONAL_INSURED",
  "WORKERS_COMPENSATION_WAIVER_OF_SUBROGATION",
  "WORKERS_COMPENSATION_PRIMARY_NON_CONTRIBUTORY",
  "WORKERS_COMPENSATION_NOTICE_OF_CANCELLATION",
  "WORKERS_COMPENSATION_WOS_ACKNOWLEDGMENT_INTERIM_WORK_AUTHORIZATION",
  "LICENSES",
  "ROOFING_LICENSE",
  "ELECTRICAL_LICENSE",
  "GC_LICENSE",
  "SOLAR_LICENSE",
  "LABOR_RECEIPT",
  "MATERIALS_RECEIPT",
  "HIC_LICENSE",
  "REPUTATION",
  "MISC",
  "COMPLIANCE_CHECKLIST",
  "COMPANY_PROFILE",
  "STATE_REGISTRATION",
  "DBA_DOCUMENTS",
  "TRAINING_CERTIFICATIONS",
  "BUSINESS_LICENSE",
  "CORRESPONDENCE",
  "COMMUNICATION_ADDENDUM",
  "DPA_DOCUMENT",
  "RRS_MSA",
  "RRC_MSA_FEB_2025",
  "RRC_MSA_FEB_2026",
  "RRC_ACH_FEB_2026",
  "SIDING_LICENSE",
  "GUTTER_LICENSE",
  "HOVER_REPORT",
  "HOVER_ESX",
  "HOVER_CAD_XML",
  "HOVER_FULL_JSON",
  "EAGLEVIEW_ESX",
  "COMPETITOR_QUOTE",
  "MEASUREMENT_REPORT",
] as const;
export const FileRoleSchema = z.enum(FileRoleValues);
export type FileRole = (typeof FileRoleValues)[number];
export const FileRole = FileRoleSchema.enum;

export type ProcessedFile = {
  id: string;
  name: string;
  type: FileType;
  role: FileRole | null;
};

export type ModifiedFile = Omit<File, "webkitRelativePath">;

export type UploadFile = {
  id: string;
  url: string;
  type: FileType;
  role: FileRole | null;
  name: string;
  file: ModifiedFile;
  uploading?: boolean;
  converting?: boolean;
  processing?: boolean;
  uploaded: boolean;
  processed?: boolean;
  metadataCreatedAt?: Date;
};

export const ContractorInsuranceFileRoles = [
  FileRole.GENERAL_LIABILITY_INSURANCE,
  FileRole.GENERAL_LIABILITY_FULL_POLICY,
  FileRole.GENERAL_LIABILITY_ADDITIONAL_INSURED,
  FileRole.GENERAL_LIABILITY_WAIVER_OF_SUBROGATION,
  FileRole.GENERAL_LIABILITY_PRIMARY_NON_CONTRIBUTORY,
  FileRole.GENERAL_LIABILITY_NOTICE_OF_CANCELLATION,
  FileRole.AUTO_INSURANCE,
  FileRole.AUTO_LIABILITY_FULL_POLICY,
  FileRole.AUTO_ADDITIONAL_INSURED,
  FileRole.AUTO_WAIVER_OF_SUBROGATION,
  FileRole.AUTO_PRIMARY_NON_CONTRIBUTORY,
  FileRole.AUTO_NOTICE_OF_CANCELLATION,
  FileRole.UMBRELLA_LIABILITY_INSURANCE,
  FileRole.UMBRELLA_LIABILITY_FULL_POLICY,
  FileRole.UMBRELLA_ADDITIONAL_INSURED,
  FileRole.UMBRELLA_WAIVER_OF_SUBROGATION,
  FileRole.UMBRELLA_PRIMARY_NON_CONTRIBUTORY,
  FileRole.UMBRELLA_NOTICE_OF_CANCELLATION,
  FileRole.UMBRELLA_FOLLOW_FORM_DOCUMENT,
  FileRole.WORKERS_COMPENSATION_INSURANCE,
  FileRole.WORKERS_COMPENSATION_FULL_POLICY,
  FileRole.WORKERS_COMPENSATION_ADDITIONAL_INSURED,
  FileRole.WORKERS_COMPENSATION_WAIVER_OF_SUBROGATION,
  FileRole.WORKERS_COMPENSATION_PRIMARY_NON_CONTRIBUTORY,
  FileRole.WORKERS_COMPENSATION_NOTICE_OF_CANCELLATION,
  FileRole.WORKERS_COMPENSATION_WOS_ACKNOWLEDGMENT_INTERIM_WORK_AUTHORIZATION,
  FileRole.INSURANCE_CERTIFICATE,
] as const satisfies FileRole[];

export const ContractorLicenseFileRoles = [
  FileRole.CONTRACTOR_LICENSE,
  FileRole.ROOFING_LICENSE,
  FileRole.ELECTRICAL_LICENSE,
  FileRole.GC_LICENSE,
  FileRole.SOLAR_LICENSE,
  FileRole.HIC_LICENSE,
  FileRole.BUSINESS_LICENSE,
  FileRole.SIDING_LICENSE,
  FileRole.GUTTER_LICENSE,
] as const satisfies FileRole[];

export const ContractorMsaFileRoles = [
  FileRole.COMMUNICATION_ADDENDUM,
  FileRole.DPA_DOCUMENT,
  FileRole.RRS_MSA,
  FileRole.RRC_MSA_FEB_2025,
  FileRole.RRC_MSA_FEB_2026,
] as const satisfies FileRole[];

export const ContractorLicenseFileRolesSchema = z.enum(ContractorLicenseFileRoles);

export const ContractorOtherFileRoles = [
  FileRole.W_9,
  FileRole.SAFETY_HEALTH_POLICY,
  FileRole.COMPLIANCE_CHECKLIST,
  FileRole.COMPANY_PROFILE,
  FileRole.CORRESPONDENCE,
  FileRole.STATE_REGISTRATION,
  FileRole.DBA_DOCUMENTS,
  FileRole.TRAINING_CERTIFICATIONS,
  FileRole.MASTER_ROOFING_SERVICES_AGREEMENT,
  FileRole.ACH,
  FileRole.RRC_ACH_FEB_2026,
  FileRole.DRIVERS_LICENSE,
  FileRole.MISC,
] as const satisfies FileRole[];

export const ContractorFileRoleValues = [
  ...ContractorInsuranceFileRoles,
  ...ContractorLicenseFileRoles,
  ...ContractorMsaFileRoles,
  ...ContractorOtherFileRoles,
] as const satisfies FileRole[];

export const ContractorFileRoleSchema = z.enum(ContractorFileRoleValues);

export type ContractorFileRoleType = z.infer<typeof ContractorFileRoleSchema>;

export const ContractorDocumentStatusValues = [
  "ACTIVE",
  "EXPIRED",
  "PENDING",
  "CORRECTIONS_NEEDED",
  "CORRECTIONS_SUBMITTED",
] as const;
export const ContractorDocumentStatusSchema = z.enum(ContractorDocumentStatusValues);
export const ContractorDocumentStatus = ContractorDocumentStatusSchema.enum;
export type ContractorDocumentStatus = (typeof ContractorDocumentStatusValues)[number];

export const ContractorDocumentStatusDisplayNames: Readonly<Record<ContractorDocumentStatus, string>> = {
  ACTIVE: "Active",
  EXPIRED: "Expired",
  PENDING: "Needs Review",
  CORRECTIONS_NEEDED: "Corrections Needed",
  CORRECTIONS_SUBMITTED: "Corrections Submitted",
};

export const ContractorPortalDocumentStatusLabels: Readonly<Record<ContractorDocumentStatus, string>> = {
  ACTIVE: "Active",
  EXPIRED: "Expired",
  PENDING: "In Review",
  CORRECTIONS_NEEDED: "Corrections Needed",
  CORRECTIONS_SUBMITTED: "In Review",
};

export const FileRoleDisplayName: Record<FileRole, string> = {
  W_9: "W-9",
  CONTRACTOR_LICENSE: "Contractor License",
  SAFETY_HEALTH_POLICY: "Safety & Health Policy",
  FULL_POLICY: "Full Policy",
  MASTER_ROOFING_SERVICES_AGREEMENT: "Master Services Agreement",
  INSURANCE_CERTIFICATE: "Certificate of Insurance",
  ACH: "ACH",
  DRIVERS_LICENSE: "Driver's License",
  GENERAL_LIABILITY_INSURANCE: "General Liability Insurance",
  GENERAL_LIABILITY_FULL_POLICY: "General Liability - Full Policy",
  GENERAL_LIABILITY_ADDITIONAL_INSURED: "General Liability - Additional Insured",
  GENERAL_LIABILITY_WAIVER_OF_SUBROGATION: "General Liability - Waiver of Subrogation",
  GENERAL_LIABILITY_PRIMARY_NON_CONTRIBUTORY: "General Liability - Primary and Non-Contributory",
  GENERAL_LIABILITY_NOTICE_OF_CANCELLATION: "General Liability - 20 Day Notice of Cancellation",
  AUTO_INSURANCE: "Auto Insurance",
  AUTO_LIABILITY_FULL_POLICY: "Auto - Full Policy",
  AUTO_ADDITIONAL_INSURED: "Auto - Additional Insured",
  AUTO_WAIVER_OF_SUBROGATION: "Auto - Waiver of Subrogation",
  AUTO_PRIMARY_NON_CONTRIBUTORY: "Auto - Primary and Non-Contributory",
  AUTO_NOTICE_OF_CANCELLATION: "Auto - 20 Day Notice of Cancellation",
  UMBRELLA_LIABILITY_INSURANCE: "Umbrella Liability Insurance",
  UMBRELLA_LIABILITY_FULL_POLICY: "Umbrella - Full Policy",
  UMBRELLA_ADDITIONAL_INSURED: "Umbrella - Additional Insured",
  UMBRELLA_WAIVER_OF_SUBROGATION: "Umbrella - Waiver of Subrogation",
  UMBRELLA_PRIMARY_NON_CONTRIBUTORY: "Umbrella - Primary and Non-Contributory",
  UMBRELLA_NOTICE_OF_CANCELLATION: "Umbrella - 20 Day Notice of Cancellation",
  UMBRELLA_FOLLOW_FORM_DOCUMENT: "Umbrella Follow Form Document",
  WORKERS_COMPENSATION_INSURANCE: "Workers' Comp Insurance",
  WORKERS_COMPENSATION_FULL_POLICY: "Workers' Comp - Full Policy",
  WORKERS_COMPENSATION_ADDITIONAL_INSURED: "Workers' Comp - Additional Insured",
  WORKERS_COMPENSATION_WAIVER_OF_SUBROGATION: "Workers' Comp - Waiver of Subrogation",
  WORKERS_COMPENSATION_PRIMARY_NON_CONTRIBUTORY: "Workers' Comp - Primary and Non-Contributory",
  WORKERS_COMPENSATION_NOTICE_OF_CANCELLATION: "Workers' Comp - 20 Day Notice of Cancellation",
  WORKERS_COMPENSATION_WOS_ACKNOWLEDGMENT_INTERIM_WORK_AUTHORIZATION:
    "Workers' Comp - WOS Acknowledgment Interim Work Authorization",
  LICENSES: "Licenses",
  ROOFING_LICENSE: "Roofing License",
  ELECTRICAL_LICENSE: "Electrical License",
  GC_LICENSE: "GC License",
  SOLAR_LICENSE: "Solar License",
  HIC_LICENSE: "HIC License",
  REPUTATION: "Reputation",
  MISC: "Other",
  QUOTE: "Quote",
  INVOICE: "Invoice",
  QUOTE_IMAGE: "Quote Image",
  SKETCH: "Sketch",
  EAGLEVIEW: "EagleView",
  CORRECTIONS_PHOTO: "Corrections Photo",
  PRICING_SHEET: "Pricing Sheet",
  MARKET_SHEET: "Market Sheet",
  LABOR_RECEIPT: "Labor Receipt",
  MATERIALS_RECEIPT: "Materials Receipt",
  CONTRACTOR_MARKET_SHEET: "Contractor Market Sheet",
  PROFILE_PHOTO: "Profile Photo",
  WORK_ORDER: "Work Order",
  COMPLIANCE_CHECKLIST: "Compliance Checklist",
  COMPANY_PROFILE: "Company Profile",
  STATE_REGISTRATION: "State Registration",
  DBA_DOCUMENTS: "DBA Documents",
  TRAINING_CERTIFICATIONS: "Training Certifications",
  BUSINESS_LICENSE: "Business License",
  CORRESPONDENCE: "Correspondence",
  COMMUNICATION_ADDENDUM: "Communication Addendum",
  DPA_DOCUMENT: "DPA Document",
  RRS_MSA: "RRS MSA",
  RRC_MSA_FEB_2025: "RRC MSA Feb. 2025",
  RRC_MSA_FEB_2026: "RRC MSA Feb. 2026",
  RRC_ACH_FEB_2026: "RRC ACH Feb. 2026",
  SIDING_LICENSE: "Siding License",
  GUTTER_LICENSE: "Gutter License",
  HOVER_REPORT: "Hover Report",
  HOVER_ESX: "Hover ESX",
  HOVER_CAD_XML: "Hover CAD XML",
  HOVER_FULL_JSON: "Hover Full JSON",
  EAGLEVIEW_ESX: "EagleView ESX",
  COMPETITOR_QUOTE: "Competitor Quote",
  MEASUREMENT_REPORT: "Measurement report",
} as const;

export const fileCreateInputSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  type: z.enum(FileType),
  tags: z.array(FileTagSchema).optional(),
  updatedAt: z.coerce.date().optional(),
  projectId: z.number().nullish(),
  workSiteId: z.number().nullish(),
  contractorProfileId: z.string().nullish(),
  taskId: z.string().nullish(),
  invoiceId: z.string().nullish(),
  billId: z.string().nullish(),
  changeOrderId: z.string().nullish(),
  creditMemoId: z.string().nullish(),
  vendorCreditId: z.string().nullish(),
  role: z.enum(FileRole).nullish(),
  expiresAt: z.coerce.date().nullish(),
  contractorDocumentStatus: ContractorDocumentStatusSchema.nullish(),
  bidQuoteId: z.string().nullish(),
  subQuoteId: z.string().nullish(),
  metadataCreatedAt: z.date().nullish(),
  messageId: z.string().nullish(),
  workOrderId: z.number().nullish(),
  pandadocDocumentId: z.string().nullish(),
  bidRequestId: z.string().nullish(),
  receiptId: z.string().nullish(),
  personId: z.string().nullish(),
  complianceEventId: z.string().nullish(),
  emailId: z.string().nullish(),
  smsMessageId: z.string().nullish(),
  estimateId: z.string().nullish(),
  quoteVersionId: z.string().nullish(),
  projectIdForCompletionOfWorkReportPdf: z.number().nullish(),
  parentFileId: z.string().nullish(),
});

export type FileCreateInput = z.infer<typeof fileCreateInputSchema>;

export const FileCreateManyInputSchema = z.object({
  properties: z.object({
    projectId: z.number().optional(),
    workSiteId: z.number().optional(),
    contractorProfileId: z.string().optional(),
    taskId: z.string().optional(),
    invoiceId: z.string().optional(),
    billId: z.string().optional(),
    changeOrderId: z.string().optional(),
    adjusterId: z.string().optional(),
    creditMemoId: z.string().optional(),
    vendorCreditId: z.string().optional(),
    bidQuoteId: z.string().optional(),
    bidId: z.number().optional(),
    tags: z.array(FileTagSchema).optional(),
    role: z.enum(FileRole).optional(),
    expiresAt: z.coerce.date().optional(),
    messageId: z.string().optional(),
    smsMessageId: z.string().optional(),
  }),
  files: z.array(
    z.object({
      id: z.string().optional(),
      name: z.string(),
      type: z.enum(FileType),
      metadataCreatedAt: z.date().optional(),
    }),
  ),
});

export type FileCreateManyInput = z.infer<typeof FileCreateManyInputSchema>;

export type FileUploadOptions = {
  projectId?: number;
  workSiteId?: number;
  contractorProfileId?: string;
  taskId?: string;
  role?: FileRole;
  maxFiles?: number;
  forSms?: boolean;
  billId?: string;
  changeOrderId?: string;
  tags?: FileTag[];
  workOrderId?: number;
  expiresAt?: Date | null;
  extractImagesFromPdf?: boolean;
  parentFileId?: string;
  /** When set, used as `contractorDocumentStatus` on create (e.g. Remi compliance new version → Needs Review). */
  contractorDocumentStatus?: ContractorDocumentStatus;
  /** Error toasts still fire when set; only the progress and success toasts are suppressed. */
  silentUploadToasts?: boolean;
};

// "CAD" is matched case-sensitively: lowercase "cad" occurs inside the cuid-style prefixes on uploaded
// filenames and would match files with no CAD in them at all. The plan-set spellings have no such
// collision, so they match in any case — Sunrun ships them as "Plans_", "PLANSET" and "SITE PLAN".
const CAD_NAME_PATTERN = /CAD/;
// Only the explicit plan-set spellings. The backfill in packages/scripts also matches "as-built",
// "CD_" and "_PS_"; those are left out here to keep the write-time rule conservative, so a few files
// that the backfill would have caught arrive untagged and get tagged by hand.
const PLAN_SET_NAME_PATTERN = /plan ?set|plans?[_ .-]/i;
// Checked before the plan-set pattern, which "Design Plans.pdf" also satisfies. Sunrun's docType
// mapping sends design plans to DESIGN_PLAN rather than PLAN_SET, so the two paths agree on the
// same document — both tags satisfy CAD_SATISFYING_FILE_TAGS either way.
const DESIGN_PLAN_NAME_PATTERN = /design ?plans?/i;

export const getTagsFromFileName = (fileName: string | null | undefined): FileTag[] => {
  if (!fileName) {
    return [];
  }
  const tags: FileTag[] = [];
  if (CAD_NAME_PATTERN.test(fileName)) {
    tags.push(FileTag.CAD_LAYOUT);
  }
  if (DESIGN_PLAN_NAME_PATTERN.test(fileName)) {
    tags.push(FileTag.DESIGN_PLAN);
  } else if (PLAN_SET_NAME_PATTERN.test(fileName)) {
    tags.push(FileTag.PLAN_SET);
  }
  return tags;
};

export const getFileType = (fileName: string): FileType | undefined => {
  const name: string = fileName.toLowerCase();
  // .dxf/.dwg are CAD drawings — documents, never photos. Without this they fall
  // through to the PHOTO fallback at call sites and get culled as invalid images.
  if (
    name.endsWith(".pdf") ||
    name.endsWith(".csv") ||
    name.endsWith(".esx") ||
    name.endsWith(".dxf") ||
    name.endsWith(".dwg")
  ) {
    return FileType.DOCUMENT;
  }
  if (isImageFile(name) || isNonRenderableImageFile(name)) {
    return FileType.PHOTO;
  }
  if (isAudioFile(name)) {
    return FileType.AUDIO;
  }
  if (isVideoFile(name)) {
    return FileType.VIDEO;
  }
  return undefined;
};

export const isImageFile = (fileName: string): boolean => {
  const name: string = fileName.toLowerCase();
  const imageExtensions = [".heic", ".heif", ".jpg", ".jpeg", ".jfif", ".png", ".webp", ".avif", ".gif", ".bmp"];
  return imageExtensions.some((ext) => name.endsWith(ext));
};

// Deliberately separate from isImageFile: these are photos, but no browser renders them in an
// <img>, so anything driving a preview must keep treating them as non-images.
export const isNonRenderableImageFile = (fileName: string): boolean => {
  const name: string = fileName.toLowerCase();
  const extensions = [".dng", ".arw", ".cr2", ".cr3", ".nef", ".orf", ".raf", ".rw2", ".tif", ".tiff"];
  return extensions.some((ext) => name.endsWith(ext));
};

export const isPdfFile = (fileName: string): boolean => fileName.toLowerCase().endsWith(".pdf");

const isAudioFile = (fileName: string): boolean => {
  const name: string = fileName.toLowerCase();
  const audioExtensions = [".mp3", ".wav", ".m4a", ".aac", ".wma", ".aiff", ".ogg"];
  return audioExtensions.some((ext) => name.endsWith(ext));
};

const isVideoFile = (fileName: string): boolean => {
  const name: string = fileName.toLowerCase();
  const videoExtensions = [".mp4", ".mov", ".webm", ".m4v", ".3gp", ".3g2", ".avi", ".mpeg", ".mpg", ".ogv", ".ts"];
  return videoExtensions.some((ext) => name.endsWith(ext));
};

/**
 * File Tagging
 */

const FileTaggingInputItemSchema = z.object({
  fileId: z.string(),
  tags: z.array(FileTagSchema),
});

export const FileTaggingInputSchema = z.array(FileTaggingInputItemSchema);

export type FileTaggingInput = z.infer<typeof FileTaggingInputSchema>;

export const createFileAndUploadToS3InputSchema = z.object({
  file: typeof Buffer !== "undefined" ? z.instanceof(Buffer).or(z.instanceof(Blob)) : z.instanceof(Blob),
  fileCreateInput: fileCreateInputSchema,
});

export type CreateFileAndUploadToS3Input = z.infer<typeof createFileAndUploadToS3InputSchema>;
